import { readApiError } from '@/shared/platform';
import { useRcmSession } from '../hooks/useRcmSession';

/**
 * What the RCM backend says about this session, in the sidebar.
 *
 * This is the first thing in the app that is not fixture data, so it is also
 * the place a wiring problem shows up. It says which of four things is true
 * rather than collapsing them into a spinner or a blank: still asking, cannot
 * reach RCM, reached RCM but this location is not a practice here, or
 * connected to a named practice.
 */
export function RcmSessionBadge({ collapsed }: { collapsed?: boolean }) {
  const { practice, permissions, locationId, isLoading, error } = useRcmSession();

  const dot = (color: string, title: string) => (
    <span
      className="inline-block h-[7px] w-[7px] shrink-0 rounded-full"
      style={{ background: color }}
      title={title}
    />
  );

  let color = 'var(--sidebar-muted)';
  let label = 'Checking RCM…';
  let detail = '';

  if (error) {
    color = 'var(--danger, #d4503f)';
    label = 'RCM unreachable';
    detail = readApiError(error)?.message ?? 'No answer from the RCM backend';
  } else if (!isLoading && !practice) {
    color = 'var(--warning, #d9a23b)';
    label = 'Not an RCM practice';
    // Naming the location turns a dead end into the one fact needed to fix
    // it: this is the id that goes in deploy/practices.seed.json. A backend
    // older than that field simply omits it, so fall back rather than
    // claiming the token carries no location.
    detail = locationId
      ? `Location ${locationId} is not set up in RCM`
      : 'This location is not set up in RCM yet';
  } else if (practice) {
    color = practice.locked ? 'var(--warning, #d9a23b)' : 'var(--ai-3)';
    label = practice.name;
    detail = [
      practice.locked ? 'Locked' : null,
      practice.kind === 'sandbox' ? 'Sandbox' : null,
      `${String(permissions.length)} permissions`,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (collapsed) {
    return (
      <div className="flex justify-center py-1">
        {dot(color, `${label}${detail ? ` — ${detail}` : ''}`)}
      </div>
    );
  }

  return (
    <div className="px-2 pt-2">
      <div className="flex items-center gap-2">
        {dot(color, label)}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[11px] font-semibold text-[var(--sidebar-ink-strong)]">
            {label}
          </span>
          {detail && (
            <span
              className="mt-px block truncate text-[10px] text-[var(--sidebar-muted)]"
              title={detail}
            >
              {detail}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
